import React from 'react';
import { Helmet } from 'react-helmet';
import DetailsHeaderText from '../components/DetailsHeaderText';
import DetailsList from '../components/DetailsList';
import TextWithImageSection from '../components/TextWithImageSection';
import Subtitle from '../components/Subtitle';
import Text from '../components/Text';
import SectionDetailsLink from '../components/SectionDetailsLink';
import Media from '../components/Media';
import dispatchImage4 from '../assets/images/dispatch/dispatch_4.png';
import dispatchImage6 from '../assets/images/dispatch/dispatch_6.png';
import dispatchImage7 from '../assets/images/dispatch/dispatch_7.png';
import dispatchImage8 from '../assets/images/dispatch/dispatch_8.png';
import dispatchImage9 from '../assets/images/dispatch/dispatch_9.png';
import dispatchImage10 from '../assets/images/dispatch/dispatch_10.png';
import dispatchImage11 from '../assets/images/dispatch/dispatch_11.png';
import dispatchImage12 from '../assets/images/dispatch/dispatch_12.jpg';
import dispatchImage13 from '../assets/images/dispatch/dispatch_13.jpg';
import SectionTitle from '../components/SectionTitle';
import { sectionIds } from './utils';
import DividerTitle from '../components/DividerTitle';
import Title from '../components/Title';
import PageFooterSlope from './PageFooterSlope';
import { routes } from '../routes';
import DetailsText from '../components/DetailsText';

const DispatchDetailsContent = () => (
    <>
        <Helmet>
            <title>realCity | dispatch – Web-Based Fleet & Public Transport Management</title>
            <meta
                name="description"
                content="realCity | dispatch is a web-based fleet management system for tracking vehicles, monitoring schedules, and managing drivers in real-time."
            />
            <meta
                name="keywords"
                content="public transport dispatch system, driver activity monitoring, timetable performance monitoring, trip modification in real-time, dispatch-to-driver communication"
            />
        </Helmet>
        <DetailsHeaderText
            category="Products"
            title="realCity | dispatch"
        />
        <TextWithImageSection
            introductorySection
            followedByDetailsText
            text={(
                <>
                    <Subtitle>Real-time operations control for modern public transport</Subtitle>
                    <Text>
                        realCity | dispatch helps public transport authorities and operators supervise daily operations
                        in real time — whether managing a small local fleet or a large city-wide network.
                    </Text>
                    <Text>
                        Fleet and depot managers gain instant visibility into vehicles, trips, delays and operational
                        risks through continuously updated dashboards and live maps. Integrated intervention tools
                        enable immediate response to service disruptions, vehicle changes and unexpected events, while
                        operational updates can be delivered directly to drivers through their on-board units.
                    </Text>
                    <Text>
                        Combining operational supervision, alerts, connection management and electric bus support,
                        realCity | dispatch helps operators make faster decisions and keep services running efficiently
                        throughout the day.
                    </Text>
                    <SectionDetailsLink navigateTo={`/#${sectionIds.contactId}`}>
                        See a demo first-hand!
                    </SectionDetailsLink>
                </>
            )}
            image={(
                <Media
                    component="img"
                    src={dispatchImage13}
                    variant="left"
                    alt="Real-time operations control for modern public transport"
                />
            )}
            alignment="image-right"
        />
        <DetailsText withBottomMargin>
            <SectionTitle>
                The platform combines the essential tools required for modern public transport operations in a single,
                integrated environment
            </SectionTitle>
            <DetailsList>
                <li>
                    Real–time operational monitoring and intervention tools, live map, messaging, alerts and vehicle
                    assignment in a single interface
                </li>
                <li>
                    Scalable from small, municipal fleets to complex urban transport networks, with references for both
                </li>
                <li>
                    Operational changes instantly reflected for driver and passenger information systems within or
                    outside the realCity ecosystem
                </li>
                <li>
                    Support for all vehicle types (buses, trolleybuses, trams, etc.) and models in a unified platform
                </li>
                <li>
                    Electric bus monitoring, charging supervision and range prediction
                </li>
                <li>
                    Advanced connection management with automated and manual wait handling
                </li>
                <li>
                    Flexible integration with standard (e.g. GTFS, GTFS-RT, SIRI, NeTEx, VDV) and legacy data formats
                </li>
                <li>
                    Data-agnostic architecture supporting existing operational systems
                </li>
                <li>
                    Web-based platform requiring only standard computer and internet connection
                </li>
                <li>
                    Demand-responsive transport extension
                </li>
            </DetailsList>
        </DetailsText>
        <DividerTitle>Key capabilities for efficient transport management</DividerTitle>
        <TextWithImageSection
            first
            text={(
                <>
                    <Title>Real-time operational overview</Title>
                    <Text>
                        Monitor vehicles, trips, delays and operational events in real time through a unified
                        operational interface. Continuously updated dashboards provide fleet managers with instant
                        visibility into network performance, schedule adherence and vehicle status.
                    </Text>
                    <Text>
                        Integrated intervention tools enable rapid response to disruptions and changing operational
                        conditions. Trips, vehicles and driver assignments can be adjusted dynamically, with all changes
                        instantly reflected across the system, including driver notifications and passenger information
                        channels.
                    </Text>
                    <Text>
                        The platform supports both smaller local operators and larger multi-line transport environments
                        with the same, consistent operational logic.
                    </Text>
                </>
            )}
            image={(
                <Media
                    component="img"
                    src={dispatchImage4}
                    variant="right"
                    alt="Real-time operational overview"
                />
            )}
            alignment="image-left"
        />
        <TextWithImageSection
            text={(
                <>
                    <Title>Interactive, customizable map</Title>
                    <Text>
                        The integrated dispatcher map provides live vehicle tracking, route visualization and stop-level
                        operational information in a configurable interface. Operators can search for vehicles, lines,
                        stops or drivers, while customizable layers and filtering options help focus on the most
                        relevant operational data. The web-based interface is accessible from any standard computer with
                        a reliable internet connection.
                    </Text>
                </>
            )}
            image={(
                <Media
                    component="img"
                    src={dispatchImage6}
                    variant="left"
                    alt="Interactive, customizable map"
                />
            )}
            alignment="image-right"
        />
        <TextWithImageSection
            text={(
                <>
                    <Title>Smart notifications and alerts</Title>
                    <Text>
                        The platform continuously monitors operational events and automatically highlights potential
                        issues requiring attention. Notifications can be generated for missing vehicles, risky
                        departures, route deviations, connection risks, electric bus charging issues and other
                        operational conflicts. Configurable thresholds and filtering options help operators prioritize
                        the most important events. OS integrated alert handling and audiovisual notifications support
                        faster operational response without the need to continuously looking at the screen.
                    </Text>
                </>
            )}
            image={(
                <Media
                    component="img"
                    src={dispatchImage7}
                    variant="right"
                    alt="Driver-friendly UI"
                />
            )}
            alignment="image-left"
        />
        <TextWithImageSection
            text={(
                <>
                    <Title>Driver messaging and communication</Title>
                    <Text>
                        realCity | dispatch includes integrated communication tools between the control center and
                        vehicles. Individual or group messages can be sent manually to onboard devices, while message
                        delivery and acknowledgement statuses remain visible for dispatchers in real time. Communication
                        functions are integrated directly into operational workflows and all operational changes are
                        sent out automatically, reducing the need for manual messaging and external communication tools.
                    </Text>
                </>
            )}
            image={(
                <Media
                    component="img"
                    src={dispatchImage8}
                    variant="left"
                    alt="Driver messaging and communication"
                />
            )}
            alignment="image-right"
        />
        <TextWithImageSection
            text={(
                <>
                    <Title>Connection management</Title>
                    <Text>
                        The platform supports the supervision and management of guaranteed passenger connections in real
                        time. Automated logic continuously evaluates feeder and fetcher trips based on live operational
                        data and configurable waiting rules. Dispatchers can manually override automated decisions
                        whenever required, ensuring operational flexibility during disruptions. The system helps
                        operators maintain reliable passenger transfers while minimizing unnecessary delays.
                    </Text>
                </>
            )}
            image={(
                <Media
                    component="img"
                    src={dispatchImage9}
                    variant="right"
                    alt="Connection management"
                />
            )}
            alignment="image-left"
        />
        <TextWithImageSection
            text={(
                <>
                    <Title>Electric bus operations support</Title>
                    <Text>
                        realCity | dispatch provides dedicated operational support for electric bus fleets, including
                        real-time battery monitoring, charging supervision and range estimation. Fleet and depot
                        managers can monitor the current and predicted operational readiness of vehicles throughout the
                        day, helping reduce the risk of service interruptions caused by insufficient charge levels. The
                        system also supports charger monitoring, charging prediction and operational alerts related to
                        charging anomalies. Electric and conventional fleets can be supervised together within the same
                        platform.
                    </Text>
                </>
            )}
            image={(
                <Media
                    component="img"
                    src={dispatchImage10}
                    variant="left"
                    alt="Electric bus operations support"
                />
            )}
            alignment="image-right"
        />
        <TextWithImageSection
            text={(
                <>
                    <Title>Demand-responsive transport support</Title>
                    <Text>
                        realCity | dispatch includes integrated support for demand-responsive and dynamically operated
                        transport services. Operators can supervise requests, passenger allocations, trip status and
                        operational execution through dedicated DRT interfaces. Requests submitted through mobile
                        applications, drivers or dispatchers are managed within the same operational environment.
                    </Text>
                </>
            )}
            image={(
                <Media
                    component="img"
                    src={dispatchImage11}
                    variant="right"
                    alt="Demand-responsive transport support"
                />
            )}
            alignment="image-left"
        />
        <TextWithImageSection
            text={(
                <>
                    <Title>Works with third-party and realCity on-board units</Title>
                    <Text>
                        Operational changes, messages and assignment updates can be transmitted directly to onboard
                        units in real time. The platform supports both the integrated realCity | drive ecosystem and
                        third-party onboard systems through flexible integration capabilities. Support for international
                        standards including GTFS, GTFS-RT, SIRI, NeTEx and VDV allows integration into diverse
                        operational environments, while existing proprietary and legacy systems can also be
                        accommodated. This data-agnostic architecture helps operators modernize operations without
                        requiring a complete replacement of existing infrastructure.
                    </Text>
                    <SectionDetailsLink navigateTo={routes.driveProduct}>
                        Discover Realcity | Drive
                    </SectionDetailsLink>
                </>
            )}
            image={(
                <Media
                    component="img"
                    src={dispatchImage12}
                    variant="left"
                    alt="Works with third-party and realCity on-board units"
                />
            )}
            alignment="image-right"
        />
        <PageFooterSlope />
    </>
);

export default DispatchDetailsContent;
